import React, { useState, useEffect, useRef } from 'react';
import { checkIn, checkOut, getMyStatus } from '../../utils/attendanceApi';
import { AttendanceRecord } from '../../types/attendance';
import { format, differenceInHours, differenceInMinutes } from 'date-fns';
import toast from 'react-hot-toast';
import { useRipple } from '../../hooks/useAnimations';

const CheckInButton: React.FC = () => {
  const [record, setRecord] = useState<AttendanceRecord | null>(null);
  const [allRecords, setAllRecords] = useState<AttendanceRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const createRipple = useRipple();

  // 상태 로드
  useEffect(() => {
    loadStatus();
  }, []);

  // 1분마다 시간 업데이트 (배터리 절약)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60초
    return () => clearInterval(timer);
  }, []);

  const loadStatus = async () => {
    try {
      const response = await getMyStatus();
      setRecord(response.data.record);
      setAllRecords(response.data.allRecords || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      console.error('상태 로드 실패:', error);
    }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const response = await checkIn();
      toast.success(response.message);
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '출근 처리 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      const response = await checkOut();
      toast.success(response.message);
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '퇴근 처리 실패');
    } finally {
      setLoading(false);
    }
  };

  // 근무시간 계산
  const getWorkDuration = () => {
    if (!record?.checkIn) return '00:00';
    const checkInTime = new Date(record.checkIn);
    const hours = differenceInHours(currentTime, checkInTime);
    const minutes = differenceInMinutes(currentTime, checkInTime) % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // 출근 타입 라벨
  const getTypeLabel = (type: string, sequence: number) => {
    if (type === 'REGULAR' || sequence === 1) return '기본근무';
    return `OT ${sequence - 1}차`;
  };

  // 출근 안 함 또는 퇴근 완료
  if (!record || !record.checkIn || record.checkOut) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border-2 border-blue-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-shrink-0">
            <span className="text-3xl">{totalCount > 0 ? '✅' : '🕐'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-blue-700">
              {totalCount > 0 ? `${totalCount}번째 퇴근 완료` : '아직 출근 안 함'}
            </p>
            <p className="text-xs text-blue-600">
              {format(currentTime, 'HH:mm')}
            </p>
            {totalCount >= 3 && (
              <p className="text-xs text-orange-600 font-semibold mt-1 leading-tight">
                ⚠️ 관리자 승인 필요
              </p>
            )}
          </div>
          <button
            onClick={(e) => {
              createRipple(e);
              handleCheckIn();
            }}
            disabled={loading}
            className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-5 py-3 rounded-xl font-bold text-sm hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 hover:scale-105 active:scale-95"
          >
            {loading ? '처리 중...' : totalCount > 0 ? `${totalCount + 1}번째 출근` : '출근하기'}
          </button>
        </div>
        
        {/* 오늘 출퇴근 이력 */}
        {allRecords.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <p className="text-xs text-gray-600 font-semibold mb-2">오늘의 출퇴근 기록</p>
            <div className="space-y-1">
              {allRecords.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">
                    {r.sequence}번째 ({getTypeLabel(r.type, r.sequence)})
                  </span>
                  <span className="text-gray-800 font-medium">
                    {format(new Date(r.checkIn!), 'HH:mm')} ~ {r.checkOut ? format(new Date(r.checkOut), 'HH:mm') : '진행중'}
                    {r.checkOut && <span className="text-indigo-600 ml-2">{r.totalHours.toFixed(1)}h</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // 출근 완료, 퇴근 전
  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-4 border-2 border-green-300">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">✅</span>
          <div>
            <p className="font-bold text-green-700 leading-tight">
              {record.sequence}번째 출근 완료!<br />
              ({getTypeLabel(record.type, record.sequence)})
            </p>
            <p className="text-xs text-green-600">
              {format(new Date(record.checkIn), 'HH:mm')} 출근
              {record.isLate && <span className="text-red-500"> (지각)</span>}
              {record.requiresApproval && <span className="text-orange-600"> ⚠️ 승인 필요</span>}
            </p>
          </div>
        </div>
        <button
          onClick={(e) => {
            createRipple(e);
            handleCheckOut();
          }}
          disabled={loading}
          className="relative overflow-hidden bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg font-bold text-sm transition-all duration-200 disabled:opacity-50 whitespace-nowrap hover:scale-105 active:scale-95"
        >
          {loading ? '처리중...' : '퇴근하기'}
        </button>
      </div>
      
      {/* 근무시간 타이머 */}
      <div className="bg-white rounded-lg p-3 flex items-center justify-between">
        <span className="text-sm text-gray-600">근무 시간</span>
        <span className="text-2xl font-black text-green-600">
          {getWorkDuration()}
        </span>
      </div>
      
      {/* 이전 출퇴근 이력 (2회차 이상일 때) */}
      {allRecords.length > 1 && (
        <div className="mt-3 pt-3 border-t border-green-200">
          <p className="text-xs text-gray-600 font-semibold mb-2">이전 출퇴근</p>
          <div className="space-y-1">
            {allRecords.slice(0, -1).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">
                  {r.sequence}번째 ({getTypeLabel(r.type, r.sequence)})
                </span>
                <span className="text-gray-800 font-medium">
                  {format(new Date(r.checkIn!), 'HH:mm')} ~ {r.checkOut ? format(new Date(r.checkOut), 'HH:mm') : '?'}
                  {r.checkOut && <span className="text-green-600 ml-2">{r.totalHours.toFixed(1)}h</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInButton;
